import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Dumbbell, BookOpen, TrendingUp, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { useWorkoutPlans, useWorkoutStats, useWorkoutSessions } from '@/hooks/useWorkout';
import { useTelegram } from '@/hooks/useTelegram';
import { EMPHASIS_META, LEVEL_META, getCyclePhase } from '@/types/workout';
import { ExerciseLibrary } from './ExerciseLibrary';

type TabKey = 'plans' | 'library' | 'progress';

export function WorkoutTab() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const [activeTab, setActiveTab] = useState<TabKey>('plans');

  function switchTab(tab: TabKey) {
    haptic('light');
    setActiveTab(tab);
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Табы внутри раздела */}
      <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-line">
        {([
          { key: 'plans',   label: 'Мои планы',  Icon: Dumbbell  },
          { key: 'library', label: 'Упражнения', Icon: BookOpen   },
          { key: 'progress',label: 'Прогресс',   Icon: TrendingUp },
        ] as { key: TabKey; label: string; Icon: any }[]).map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => switchTab(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
              activeTab === key
                ? 'bg-emerald-glow/15 text-emerald-glow'
                : 'text-ink-dim'
            }`}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'plans'    && <PlansTab />}
        {activeTab === 'library'  && <ExerciseLibrary />}
        {activeTab === 'progress' && <ProgressTab />}
      </div>
    </div>
  );
}

// ─── Вкладка "Мои планы" ────────────────────────────────────────────────────

function PlansTab() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { plans, loading, deletePlan } = useWorkoutPlans();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-emerald-glow" />
      </div>
    );
  }

  return (
    <div className="py-4 space-y-4 px-4">
      {/* Кнопки создать план */}
      {plans.length < 3 && (
        <div className="space-y-2">
          <motion.button
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => { haptic('light'); nav('/workout/create'); }}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-emerald-glow/20 to-sky-glow/10 border border-emerald-glow/30 active:scale-[0.98] transition-all"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-glow/20 flex items-center justify-center shrink-0">
              <Plus size={20} className="text-emerald-glow" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-sm">Составить с AI</div>
              <div className="text-[11px] text-ink-muted mt-0.5">AI подберёт упражнения под твои цели</div>
            </div>
            <ChevronRight size={16} className="text-ink-dim" />
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            onClick={() => { haptic('light'); nav('/workout/custom'); }}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-bg-card border border-line active:scale-[0.98] transition-all"
          >
            <div className="w-10 h-10 rounded-xl bg-bg-elevated flex items-center justify-center shrink-0 text-xl">
              📋
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-sm">Свой план</div>
              <div className="text-[11px] text-ink-muted mt-0.5">Внеси свою программу вручную</div>
            </div>
            <ChevronRight size={16} className="text-ink-dim" />
          </motion.button>
        </div>
      )}

      {plans.length === 0 && (
        <div className="text-center py-8 space-y-2">
          <div className="text-4xl">🏋️</div>
          <div className="text-sm text-ink-muted">Планов пока нет</div>
          <div className="text-[11px] text-ink-dim">Создай первый — займёт 2 минуты</div>
        </div>
      )}

      {/* Список планов */}
      {plans.map((plan, i) => (
        <motion.div
          key={plan.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.06 }}
          className="bg-bg-card border border-line rounded-2xl overflow-hidden"
        >
          {/* Заголовок плана */}
          <button
            onClick={() => { haptic('light'); nav(`/workout/plan/${plan.id}`); }}
            className="w-full p-4 flex items-start gap-3 active:bg-bg-elevated transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-glow/10 flex items-center justify-center shrink-0">
              <Dumbbell size={18} className="text-emerald-glow" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="font-semibold text-sm truncate">{plan.name}</div>
              <div className="text-[11px] text-ink-muted mt-0.5">
                {LEVEL_META[plan.level].label} · {plan.days_per_week}×/нед
              </div>
              {/* Фаза цикла */}
              {(() => {
                const phase = getCyclePhase(plan.current_week);
                const cyclePos = ((plan.current_week - 1) % 4) + 1;
                return (
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className={`flex items-center gap-1 text-[10px] font-semibold ${phase.color}`}>
                      <span>{phase.emoji}</span>
                      <span>{phase.label}</span>
                    </div>
                    <div className="flex gap-0.5">
                      {[1,2,3,4].map(i => (
                        <div key={i} className={`w-4 h-1 rounded-full transition-colors ${i <= cyclePos ? 'bg-emerald-glow' : 'bg-bg-elevated'}`} />
                      ))}
                    </div>
                    <span className="text-[9px] text-ink-dim">нед {plan.current_week}</span>
                  </div>
                );
              })()}
            </div>
            <ChevronRight size={16} className="text-ink-dim shrink-0 mt-1" />
          </button>

          {/* Тренировки из плана */}
          {plan.plan_data?.sessions && (
            <div className="border-t border-line px-3 py-2 space-y-1">
              {plan.plan_data.sessions.map((session, si) => (
                <button
                  key={si}
                  onClick={() => {
                    haptic('medium');
                    nav(`/workout/session/${plan.id}/${si}`);
                  }}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-xl active:bg-bg-elevated transition-colors"
                >
                  <div className="w-6 h-6 rounded-lg bg-emerald-glow/10 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-emerald-glow">{si + 1}</span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-xs font-medium truncate">{session.name}</div>
                    <div className="text-[10px] text-ink-dim">
                      {session.exercises.length} упражнений
                    </div>
                  </div>
                  <div className="text-[10px] text-emerald-glow font-medium shrink-0">
                    Начать →
                  </div>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      ))}

      {plans.length === 3 && (
        <p className="text-[11px] text-ink-dim text-center">
          Максимум 3 активных плана. Удали один чтобы создать новый.
        </p>
      )}

      {/* Дисклеймер */}
      <div className="rounded-xl bg-bg-elevated border border-line p-3">
        <p className="text-[10px] text-ink-dim leading-relaxed">
          ⚠️ Планы носят ознакомительный характер. Для максимальной эффективности и безопасности обратись к сертифицированному тренеру.
        </p>
      </div>
    </div>
  );
}

// ─── Вкладка "Прогресс" — краткая версия (полная на отдельном экране) ────────

function ProgressTab() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { stats, loading } = useWorkoutStats();
  const { sessions, deleteSession } = useWorkoutSessions(5);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-emerald-glow" />
      </div>
    );
  }

  return (
    <div className="py-4 space-y-4 px-4">
      {/* Карточки-метрики */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          label="Тренировок всего"
          value={stats.totalSessions.toString()}
          emoji="🏋️"
        />
        <MetricCard
          label="Серия (недели)"
          value={stats.currentStreak.toString()}
          emoji="🔥"
        />
        <MetricCard
          label="Тоннаж всего"
          value={`${Math.round(stats.totalTonnage / 1000 * 10) / 10} т`}
          emoji="⚡"
        />
        <MetricCard
          label="На этой неделе"
          value={`${stats.thisWeekSessions} сессии`}
          emoji="📅"
        />
      </div>

      {/* Последние тренировки */}
      {sessions.length > 0 && (
        <div>
          <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold mb-2 px-1">
            Последние тренировки
          </div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id}>
                <AnimatePresence>
                  {pendingDelete === s.id ? (
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 flex items-center gap-2"
                    >
                      <div className="flex-1 text-[12px] text-ink-muted">Удалить тренировку?</div>
                      <button
                        onClick={() => setPendingDelete(null)}
                        className="text-[11px] text-ink-dim px-2 py-1 border border-line rounded-lg"
                      >
                        Отмена
                      </button>
                      <button
                        onClick={() => { haptic('success'); deleteSession(s.id); setPendingDelete(null); }}
                        className="text-[11px] text-rose-400 font-semibold px-2 py-1 border border-rose-500/40 rounded-lg"
                      >
                        Удалить
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="row"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="bg-bg-card border border-line rounded-xl p-3 flex items-center gap-3"
                    >
                      <button
                        onClick={() => { haptic('light'); nav(`/workout/log/${s.id}`); }}
                        className="flex items-center gap-3 flex-1 min-w-0 active:opacity-70 transition-opacity"
                      >
                        <div className="text-xl shrink-0">
                          {s.rating === 5 ? '💪' : s.rating === 4 ? '🙂' : s.rating === 3 ? '😐' : '😕'}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="text-sm font-medium truncate">{s.session_name ?? 'Тренировка'}</div>
                          <div className="text-[11px] text-ink-muted">
                            {Math.round(s.total_tonnage_kg)} кг · {s.total_sets} подх. · {formatDuration(s.duration_seconds)}
                          </div>
                        </div>
                        <div className="text-[10px] text-ink-dim shrink-0">
                          {formatDate(s.started_at)}
                        </div>
                      </button>
                      <button
                        onClick={() => { haptic('light'); setPendingDelete(s.id); }}
                        className="p-1.5 rounded-lg text-rose-400/60 active:text-rose-400 transition-colors shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      )}

      {sessions.length === 0 && (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">📈</div>
          <div className="text-sm text-ink-muted">Выполни первую тренировку</div>
          <div className="text-[11px] text-ink-dim mt-1">и здесь появится твой прогресс</div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, emoji }: { label: string; value: string; emoji: string }) {
  return (
    <div className="bg-bg-card border border-line rounded-xl p-3">
      <div className="text-xl mb-1">{emoji}</div>
      <div className="font-bold text-lg leading-tight">{value}</div>
      <div className="text-[10px] text-ink-dim mt-0.5">{label}</div>
    </div>
  );
}

function formatDuration(seconds: number | null) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  return `${m} мин`;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'short' });
}
